import React from 'react';
import { bffQuery, errorCode } from '../lib/bffClient.js';
import { startConference } from '../lib/jitsiEmbed.js';
import { useTranslation } from '../i18n/I18nProvider.jsx';
import { VIDEO_VISIT_SESSION_QUERY } from '../screens/Visit/Visit.shared.js';

// ویزیت تصویری — the dentist's side of the video visit.
//
// ── Nothing starts by itself ────────────────────────────────────────────────
// Same rule as every AI call on this screen, for a different reason: a camera
// that turns itself on when a screen opens is a camera pointed at a room nobody
// agreed to show. `start()` is a button press, always.
//
// ── The ordering below is copied from useServerSpeechToText, deliberately ────
// That hook learned four things the hard way, and every one of them applies to
// a call as much as to a microphone:
//
//   1. REFRESH THE TOKEN FIRST. A session one refresh away from working must
//      not fail as "the call is unavailable" — the BFF would refuse the query,
//      and the dentist has no way to tell that from a broken feature.
//   2. GUARD THE DOUBLE TAP. Two conferences for one room is two cameras and
//      two microphones in one browser tab, echoing.
//   3. TEARDOWN DETACHES HANDLERS BEFORE DISPOSING. A late event from the
//      session being torn down must not clear flags a NEXT session has set.
//   4. TEAR DOWN ON UNMOUNT. A conference left running past unmount holds the
//      camera, and the tab's recording indicator stays lit on a screen that no
//      longer shows a call.

/** What the panel is doing, in one word the UI can switch on. */
export const CALL_IDLE = 'idle';
export const CALL_STARTING = 'starting';
export const CALL_LIVE = 'live';
export const CALL_ERROR = 'error';

/**
 * The video visit for one appointment.
 *
 * `available` is null until the first `probe()`: not-yet-asked is a different
 * state from "this deployment has no media server", and rendering «تماس
 * تصویری» for a deployment that cannot make one is the bug that distinction
 * prevents.
 */
export function useVideoVisit({ patientId, appointmentId, editable }) {
  // The call's own chrome follows the panel language. Without it Jitsi stayed
  // on jitsiEmbed's 'fa' default, so the English (and French, German) UI
  // opened a Persian call. Jitsi ships fa, en, fr and de.
  const { locale } = useTranslation();
  const [state, setState] = React.useState(CALL_IDLE);
  const [error, setError] = React.useState(null);
  const [available, setAvailable] = React.useState(null);

  const apiRef = React.useRef(null);
  const openingRef = React.useRef(false);
  const containerRef = React.useRef(null);

  /**
   * Take the conference down.
   *
   * Handlers are removed BEFORE `dispose()`: disposing fires
   * `readyToClose`/`videoConferenceLeft`, and a handler still attached would
   * run against state a later session may already own.
   */
  const teardown = React.useCallback(() => {
    const api = apiRef.current;
    apiRef.current = null;
    openingRef.current = false;
    if (!api) return;
    try {
      api.removeAllListeners?.();
      api.dispose?.();
    } catch {
      // A conference that is already gone is the state we wanted.
    }
  }, []);

  /**
   * Does this deployment offer video visits, for this visit?
   *
   * Cheap, mints nothing, and safe to call on mount — the query returns null
   * rather than an error when the feature is off.
   */
  const probe = React.useCallback(async () => {
    if (!patientId || !appointmentId || !editable) {
      setAvailable(false);
      return;
    }
    try {
      const data = await bffQuery(VIDEO_VISIT_SESSION_QUERY, { patientId, appointmentId });
      setAvailable(Boolean(data?.videoVisitSession));
    } catch {
      // A probe that failed is not a feature that is off, but for the purpose
      // of drawing a button the two are the same: do not offer what we could
      // not confirm.
      setAvailable(false);
    }
  }, [patientId, appointmentId, editable]);

  const start = React.useCallback(async () => {
    if (openingRef.current || apiRef.current) return;
    openingRef.current = true;
    setError(null);
    setState(CALL_STARTING);

    try {
      // A fresh session BEFORE anything else. The token is short-lived by
      // design, so the query is re-run rather than its answer cached — and
      // re-running it is also what re-checks that this visit is still open.
      const data = await bffQuery(VIDEO_VISIT_SESSION_QUERY, { patientId, appointmentId });
      const session = data?.videoVisitSession;
      if (!session) {
        setAvailable(false);
        throw new Error('unavailable');
      }

      // The panel mounts its container in the same render that flips state to
      // STARTING, so the node exists by the time the await above resolves. If
      // it does not, the panel was closed mid-open — stop rather than attach a
      // conference to nothing.
      const parentNode = containerRef.current;
      if (!parentNode) {
        openingRef.current = false;
        setState(CALL_IDLE);
        return;
      }

      const api = await startConference({
        domain: session.domain,
        roomName: session.roomName,
        token: session.token,
        displayName: session.displayName,
        parentNode,
        // The dentist is already inside the visit; a device-check screen here
        // is a second confirmation of a decision already made.
        prejoin: false,
        lang: locale,
      });

      // Closed while the script was loading.
      if (!containerRef.current) {
        try { api.dispose?.(); } catch { /* already gone */ }
        openingRef.current = false;
        setState(CALL_IDLE);
        return;
      }

      apiRef.current = api;
      openingRef.current = false;
      setState(CALL_LIVE);

      // «قطع تماس» inside the Jitsi UI, and any other end the iframe decides
      // on. Without this the panel would keep showing a dead conference.
      api.addListener('readyToClose', () => {
        teardown();
        setState(CALL_IDLE);
      });
      api.addListener('errorOccurred', (event) => {
        // Only a fatal error takes the call down; Jitsi reports recoverable
        // ones (a brief reconnect) through the same channel.
        if (event?.error?.isFatal) {
          teardown();
          setError('connection');
          setState(CALL_ERROR);
        }
      });
    } catch (err) {
      teardown();
      const code = errorCode(err);
      setError(
        code === 'VISIT_NOT_EDITABLE' ? 'visitOver'
          : err?.message === 'unavailable' ? 'unavailable'
            : 'connection',
      );
      setState(CALL_ERROR);
    }
  }, [patientId, appointmentId, locale, teardown]);

  /** «پایان تماس» from OUR chrome, as opposed to Jitsi's own hangup. */
  const stop = React.useCallback(() => {
    const api = apiRef.current;
    // Ask the conference to leave properly first so the other side sees a
    // departure rather than a silence; teardown then disposes regardless.
    try { api?.executeCommand?.('hangup'); } catch { /* disposing anyway */ }
    teardown();
    setState(CALL_IDLE);
    setError(null);
  }, [teardown]);

  // A conference outlives the screen unless something ends it. This is the
  // camera indicator staying lit after the dentist navigated away.
  React.useEffect(() => teardown, [teardown]);

  // Moving to another visit must not leave the previous visit's call running in
  // a container that now belongs to a different patient.
  React.useEffect(() => {
    teardown();
    setState(CALL_IDLE);
    setError(null);
  }, [appointmentId, teardown]);

  return {
    state, error, available, containerRef,
    live: state === CALL_LIVE,
    busy: state === CALL_STARTING,
    probe, start, stop,
  };
}
